import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Text,
  Button,
  Group,
  Badge,
  Select,
  Table,
  LoadingOverlay,
  Card,
  ThemeIcon,
  Center,
  ScrollArea,
} from "@mantine/core";
import { IconArrowLeft, IconTicket, IconFilter } from "@tabler/icons-react";
import { API_BASE_URL } from "../../../../config/baseUrls";

// --- API HELPERS ---
const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
  "Content-Type": "application/json",
});

export default function AddOnEventPasses({ productId }) {
  const navigate = useNavigate();

  // Data State
  const [passes, setPasses] = useState([]);
  const [availableConfigs, setAvailableConfigs] = useState([]);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  // Filter State
  const [filters, setFilters] = useState({
    status: "",
    configId: "",
  });

  // --- LOGIC: FETCH DATA ---
  const fetchPasses = async () => {
    if (!productId) return; // Guard clause

    try {
      setLoading(true);
      const queryParams = new URLSearchParams(filters);

      const response = await fetch(
        `${API_BASE_URL}/admin/products/${productId}/addon-passes?${queryParams}`,
        { headers: getAuthHeaders() },
      );
      const data = await response.json();

      if (data.success) {
        setPasses(data.data.passes);
        setAvailableConfigs(data.data.configs);
      }

      // Fetch product info if missing
      if (!product) {
        const prodRes = await fetch(
          `${API_BASE_URL}/admin/products/${productId}`,
          { headers: getAuthHeaders() },
        );
        const prodData = await prodRes.json();
        if (prodData.success) setProduct(prodData.data);
      }
    } catch (error) {
      console.error("Error fetching passes:", error);
    } finally {
      setLoading(false);
    }
  };

  // Auto-fetch on filter change
  useEffect(() => {
    fetchPasses();
  }, [productId, filters]);

  // --- UI HELPERS ---
  const getStatusBadge = (status) => {
    const map = {
      CONFIRMED: { color: "green", label: "Confirmed" },
      USED: { color: "blue", label: "Used" },
      CANCELLED: { color: "red", label: "Cancelled" },
      PENDING: { color: "yellow", label: "Pending" },
    };
    const conf = map[status] || { color: "gray", label: status };
    return (
      <Badge color={conf.color} variant="light">
        {conf.label}
      </Badge>
    );
  };

  return (
    <Box className="space-y-6">
      {/* HEADER */}
      <Group justify="space-between" mb="lg">
        <Group>
          <Box>
            <Text size="xl" fw={800} className="ota-gradient-text">
              Add-On Sales
            </Text>
            {product && (
              <Text size="sm" c="dimmed">
                Event: {product.title}
              </Text>
            )}
          </Box>
        </Group>
      </Group>

      {/* FILTERS */}
      <Paper p="md" radius="md" withBorder bg="gray.0">
        <Group align="end">
          <Select
            label="Filter by Activity"
            placeholder="All Activities"
            data={[
              { value: "", label: "All Activities" },
              ...availableConfigs.map((c) => ({ value: c.id, label: c.title })),
            ]}
            value={filters.configId}
            onChange={(v) => setFilters({ ...filters, configId: v || "" })}
            searchable
            style={{ flex: 1 }}
          />
          <Select
            label="Filter by Status"
            placeholder="Status"
            data={[
              { value: "", label: "All (Confirmed & Used)" },
              { value: "CONFIRMED", label: "Confirmed" },
              { value: "USED", label: "Used" },
              { value: "CANCELLED", label: "Cancelled" },
            ]}
            value={filters.status}
            onChange={(v) => setFilters({ ...filters, status: v || "" })}
            style={{ width: 200 }}
          />
          <Button
            variant="light"
            color="blue"
            onClick={fetchPasses}
            leftSection={<IconFilter size={16} />}
          >
            Refresh
          </Button>
        </Group>
      </Paper>

      {/* DATA TABLE */}
      <Box pos="relative" mih={300}>
        <LoadingOverlay
          visible={loading}
          overlayProps={{ radius: "sm", blur: 2 }}
        />

        {passes.length === 0 && !loading ? (
          <Paper p="xl" withBorder radius="md" ta="center" bg="gray.0">
            <ThemeIcon
              size={60}
              radius="xl"
              color="gray"
              variant="light"
              mb="md"
            >
              <IconTicket size={30} />
            </ThemeIcon>
            <Text fw={600} size="lg">
              No Passes Found
            </Text>
            <Text c="dimmed">
              Try adjusting your filters or checking back later.
            </Text>
          </Paper>
        ) : (
          <Card withBorder radius="md" padding="0">
            <ScrollArea>
              <Table stickyHeader highlightOnHover verticalSpacing="sm">
                {/* FIX: Changed bg="gray.50" to bg="gray.1" (Mantine uses 0-9) */}
                <Table.Thead bg="gray.1">
                  <Table.Tr>
                    <Table.Th>Activity / Add-On</Table.Th>
                    <Table.Th>Timeslot</Table.Th>
                    <Table.Th>Customer</Table.Th>
                    <Table.Th>Status</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {passes.map((pass) => (
                    <Table.Tr key={pass.id}>
                      <Table.Td>
                        <Text fw={600} size="sm">
                          {pass.config?.title}
                        </Text>
                        <Text
                          size="xs"
                          c="dimmed"
                          style={{ fontFamily: "monospace" }}
                        >
                          ID: {pass.passNumber}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">
                          {new Date(pass.timeslot.startTime).toLocaleTimeString(
                            [],
                            { hour: "2-digit", minute: "2-digit" },
                          )}{" "}
                          -
                          {new Date(pass.timeslot.endTime).toLocaleTimeString(
                            [],
                            { hour: "2-digit", minute: "2-digit" },
                          )}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {new Date(
                            pass.timeslot.startTime,
                          ).toLocaleDateString()}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{pass.user?.email}</Text>
                        <Text size="xs" c="dimmed">
                          {pass.user?.phoneNumber || "N/A"}
                        </Text>
                      </Table.Td>
                      <Table.Td>{getStatusBadge(pass.status)}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </ScrollArea>
          </Card>
        )}
      </Box>
    </Box>
  );
}
